/**
 * Composant InteractiveReportAG
 *
 * Ce composant hybride combine :
 * - La logique de chargement de données d'InteractiveReport (via useInteractiveReports)
 * - L'affichage avec AG Grid d'AgGridContainer (comme Datatable)
 *
 * Il permet d'utiliser les fonctionnalités d'InteractiveReport (groupBy, aggregates, breakRows, etc.)
 * avec l'interface utilisateur et les performances d'AG Grid.
 *
 * @example
 * <InteractiveReportAG
 *   sjmoCode="MODULE_CODE"
 *   definitionId="interactive-report-id"
 * />
 */
import React, { FC, useRef, useMemo, useCallback, useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  FilterModel,
  SortModelItem,
  ColumnState,
  GetRowIdParams,
  ValueSetterParams,
} from "ag-grid-community";
import AgGridContainer from "./AgGridContainer";
import { mapFiltersToAgGridFilterModel } from "./utils/queryUtils";
import { DatatableFocusState } from "types/Focus";
import {
  ComponentReadOnly,
  ComponentState,
  FilterBarDefinition,
  InteractiveReportConfiguration,
} from "types/Component";
import { Pojo } from "types/Galaxy";
import { PagedResource, FilterBar, PagedResourceWithAggregates } from "types/Search";
import { RSQLCriteria } from "rsql-criteria-typescript";
import { CancelTokenSource } from "axios";
import { findViewComplex } from "api";
import {
  useInteractiveReports,
  IR_MINIMUM_BATCH_SIZE,
  searchTerm,
  initialState as irInitialState,
} from "composants/datatableAGGrid/useInteractiveReport";
import {
  AggregateProperties,
  FilterProperties,
  GroupByFunctionParameter,
} from "types/InteractiveReport";
import { AGFilter } from "./Grid.types";
import { convertTypeCompoColumnIr, getRSQLFilter } from "./utils/queryUtils";
import { uuidv4 } from "utils/uuid.utils";
import { createCustomComponentState } from "utils/component.utils";
import { GSBuilder } from "utils/query.utils";
import { SaveDialog } from "composants/datatable/dialog/SaveDialog";
import { InteractiveReportAGProps } from "./Grid.types";
import { initialDTState } from "./GridDtReducer";
import { getGridDtKey, GRID_DT_CACHE, GridDtCacheState } from "./hoc/withGridDtCache";
import { RSQLOperator } from "utils/query.utils";
import { HighlightProperties } from "composants/datatable/dialog/HighlightDialog";
import toaster from "composants/toaster/toaster";
import { t } from "utils/i18n";
import { useGalaxieCallbackListener } from "containers/galaxy/GalaxieContextListener";

function getRowId(params: GetRowIdParams) {
  return params.data.uuid;
}

/**
 * Convertit une InteractiveReportConfiguration en DatatableFocusState
 */
function convertFocusToDatatableFocus(
  config: InteractiveReportConfiguration,
  index: number,
  columns: ComponentState[],
): DatatableFocusState {
  return {
    focusId: config.id?.toString() || `focus-${index}`,
    moduleId: "",
    focusLabel: config.label,
    focusCode: config.label,
    focusNature: config.typeFocus ? config.typeFocus.toUpperCase() : "",
    position: config.position || index,
    privilegie: false,
    perso: config.typeFocus === "user",
    pageSize: 25,
    insertable: false,
    updatatable: false,
    deletable: false,
    filterable: true,
    filterVisible: true,
    cssClass: "",
    rowExpandable: false,
    injectRowClass: "",
    draggable: false,
    resizableColumns: true,
    draggableColumns: true,
    highlights: config.highlights || [],
    groupByColumns: config.groupBy.groupBy || null,
    groupByFunctionList: config.groupBy.functions || null,
    filters: (config.filters as any) || [],
    sort: config.sortBy
      .map((sort) => `${sort.column} ${sort.sort === "ASC" ? "asc" : "desc"}`)
      .join(","),
    // Les filtres "static" sont gérés en dehors des filtres courants.
    currentFilter: mapFiltersToAgGridFilterModel(
      ((config.filters as unknown as FilterProperties[]) || []).filter(
        (filter) => filter.type !== "static",
      ),
      columns,
    ),
    currentOrderBy: config.sortBy.map((sort) => ({
      colId: sort.column,
      sort: sort.sort === "ASC" ? "asc" : "desc",
    })),
    currentPin: config.currentPin || [],
    aggregates: config.aggregates || [],
    defaultOpenBreakRows: config.defaultOpenBreakRows ?? false,
  };
}

/**
 * Fonction fetchData adaptée pour InteractiveReport utilisant findViewComplex
 */
async function fetchDataIR(props: {
  sjmoCode: string;
  tableName: string;
  ctrlkey: string;
  currentFocus?: DatatableFocusState;
  query: RSQLCriteria;
  startIndex: number;
  stopIndex: number;
  reset: boolean;
  cancelToken: CancelTokenSource;
  oelColumns: string[];
  filterBar: FilterBar | undefined;
  breakRows?: string[];
  groupByConfig?: { columns: string[]; functionList: GroupByFunctionParameter[] };
  columns?: ComponentState[];
  aggregates?: AggregateProperties[];
  aggregateFilter?: RSQLCriteria;
}): Promise<PagedResourceWithAggregates<Pojo>> {
  const {
    tableName,
    query,
    startIndex,
    stopIndex,
    cancelToken,
    breakRows = [],
    groupByConfig,
    aggregates = [],
    columns = [],
    aggregateFilter,
  } = props;

  if (props.reset) {
    return { data: [], meta: { totalRecords: 0, size: 0, aggregates: [] } };
  }

  const size = Math.max(stopIndex - startIndex, IR_MINIMUM_BATCH_SIZE);
  const includes =
    groupByConfig && groupByConfig.columns.length > 0
      ? [...groupByConfig.columns]
      : columns.map((col) => col.column);

  // Ajouter les fonctions de groupement si présentes
  if (groupByConfig?.functionList && groupByConfig.functionList.length > 0) {
    for (const groupByFn of groupByConfig.functionList) {
      includes.push(groupByFn.label);
    }
  }

  try {
    const groupByParam =
      groupByConfig && (groupByConfig.columns.length > 0 || groupByConfig.functionList.length > 0)
        ? {
            columns: groupByConfig.columns,
            functionList: groupByConfig.functionList,
          }
        : {
            columns: [],
            functionList: [],
          };

    const res = await findViewComplex(
      {
        tableName,
        first: startIndex,
        size,
        includes,
        filter: query.build(),
        groupBy: groupByParam,
        aggregates,
        breakRows,
        aggregateFilter: aggregateFilter?.build(),
      },
      cancelToken,
    );

    for (const row of res.data.data) {
      row.uuid = uuidv4();
    }

    return {
      data: res.data.data,
      meta: {
        totalRecords: res.data.meta.totalRecords,
        size: res.data.meta.size || size,
        aggregates: res.data.meta.aggregates || [],
      },
    };
  } catch (e) {
    console.error("Erreur lors de la récupération des données", e);
    return { data: [], meta: { totalRecords: 0, size: 0, aggregates: [] } };
  }
}

// Fonction getRSQLFilter adaptée pour InteractiveReport
function getRSQLFilterIR(
  interactions: Record<string, any>,
  searchValues: AGFilter,
  sortValues: Record<string, any>,
  breakColumns: string[],
  additionnalClause: RSQLCriteria | undefined,
  columns: Record<string, ComponentState>,
): RSQLCriteria {
  // Utiliser la fonction standard de ReadOnlyTable
  const rsql = getRSQLFilter(
    interactions,
    searchValues,
    sortValues,
    breakColumns,
    additionnalClause,
    columns,
  );

  // On Ajoute pas les filtres spécifiques d'InteractiveReport
  // Ils sont passés à l'AgGrid lorsque qu'on change de focus
  return rsql;
}

const InteractiveReportAG: FC<InteractiveReportAGProps> = ({
  sjmoCode,
  definitionId,
  onAfterSaveDatatable,
  onAfterDeleteDatatable,
  onRowClickParent,
  setSelectedRowsParent,
  onChangeSelectedEntities,
  clearCacheOnClose = false,
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const inputSearchRef = useRef<HTMLInputElement | null>(null);
  const tableRef = useRef<any>(null); // Pas utilisé mais requis par useInteractiveReports
  const ctrlkey = useMemo(() => `interactiveReport:${definitionId}`, [definitionId]);

  const {
    state: irState,
    focusAction,
    columnsAction,
    groupByAction,
    dispatch,
  } = useInteractiveReports(sjmoCode, definitionId, inputSearchRef, tableRef);
  const state = useMemo(() => (irState ? irState : irInitialState), [irState]);
  const [gridInitialState, setGridInitialState] = useState<GridDtCacheState | null>(null);
  const [dirtyRow, setDirtyRow] = useState<Record<number, Partial<Pojo>>>({});

  const [triggerRefresh, settriggerRefresh] = useState<number>(0);
  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false);

  useEffect(() => {
    if (!state.tableName) return;

    const gridDtKey = getGridDtKey(sjmoCode, ctrlkey, state.tableName);
    GRID_DT_CACHE.get(gridDtKey).then((val) => {
      setGridInitialState(val || initialDTState);
    });
  }, [sjmoCode, ctrlkey, state.tableName]);

  useEffect(() => {
    return () => {
      if (clearCacheOnClose && state.tableName) {
        GRID_DT_CACHE.del(getGridDtKey(sjmoCode, ctrlkey, state.tableName));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!clearCacheOnClose && state.tableName) {
      GRID_DT_CACHE.get(getGridDtKey(sjmoCode, ctrlkey, state.tableName)).then((oldState) => {
        GRID_DT_CACHE.set(getGridDtKey(sjmoCode, ctrlkey, state.tableName), {
          ...(oldState || initialDTState),
          dirty: dirtyRow,
        });
      });
    }
  }, [clearCacheOnClose, sjmoCode, ctrlkey, state.tableName, dirtyRow]);

  // Convertir les configurations en focus Datatable
  const focusList = useMemo<DatatableFocusState[]>(() => {
    const focusListLocal = state.focus.map((config, index) =>
      convertFocusToDatatableFocus(config, index, state.columns),
    );
    return focusListLocal;
  }, [state.focus]);

  // Focus actif
  const currentFocus = useMemo(() => {
    if (state.activeFocus >= 0 && state.activeFocus < focusList.length) {
      // On extrait de state tous les éléments qui constituent un focus dans une variable
      const curentFocusWithUserChange = {
        id: focusList[state.activeFocus].focusId,
        filters: state.search.filters,
        currentFilter: state.search.filters,
        sortBy: state.sortBy,
        groupBy: {
          groupBy: state.groupBy.columns,
          functions: state.groupBy.functionList,
        },
        aggregates: state.aggregates,
        breakRows: state.breakRows,
        computedColumns: state.computedColumns,
        highlights: state.highlights,
        currentPin: state.currentPin,
        defaultOpenBreakRows: state.defaultOpenBreakRows,
        typeFocus: state.focus[state.activeFocus].typeFocus,
        label: focusList[state.activeFocus].focusLabel,
        position: focusList[state.activeFocus].position,
      } as InteractiveReportConfiguration;

      const currentFocusLocal = convertFocusToDatatableFocus(
        curentFocusWithUserChange,
        state.activeFocus,
        state.columns,
      );
      return currentFocusLocal;
    }
    return focusList[0];
  }, [state]);

  // Récupération des filtres statiques à appliquer dans additionnalClause
  const staticFiltersClause = useMemo(() => {
    const staticFilters = ((currentFocus?.filters as unknown as FilterProperties[]) || []).filter(
      (filter) => filter.type === "static",
    );

    if (staticFilters.length === 0) return undefined;

    return searchTerm(state.columns, "", "", staticFilters);
  }, [currentFocus?.filters, state.columns]);

  const groupByState = useMemo(() => {
    return {
      columns: currentFocus?.groupByColumns || [],
      functionList: currentFocus?.groupByFunctionList || [],
    };
  }, [currentFocus?.groupByColumns, currentFocus?.groupByFunctionList]);

  const effectiveDefaultOpenBreakRows = useMemo(() => {
    return state.defaultOpenBreakRows ?? false;
  }, [state.defaultOpenBreakRows]);

  const groupByFnColumn = useMemo(() => {
    return state.groupBy.functionList.map((fnList) => {
      return createCustomComponentState(fnList.label.toLowerCase(), "NUMBER", fnList.label, []);
    });
  }, [state.groupBy.functionList]);

  // Définition pour AgGridContainer
  const definition = useMemo(
    () => ({
      focus: focusList,
      columns: [
        ...state.columns.map((col) => {
          const size =
            state.focus[state.activeFocus]?.includesSize?.[col.column] || col.contentSize;

          // Récupération des styles de la colonne via highlights
          const options = { ...col.options };
          const selectedHighlight = state.highlights.filter((h) => h.column === col.column);
          const style = {};
          selectedHighlight.forEach((highlight) => {
            style[highlight.term] = {
              backgroundColor: highlight.backgroundColor,
              color: highlight.color,
            };
          });
          options.styleContextuel = { [col.column]: style };

          return {
            ...convertTypeCompoColumnIr(col),
            options: options,
            contentSize: size,
            labelSize: size,
          };
        }),
      ],
      selectedFocus: currentFocus?.focusId || null,
    }),
    [
      focusList,
      state.columns,
      state.groupBy.columns,
      state.focus,
      state.activeFocus,
      state.highlights,
      groupByFnColumn,
      currentFocus?.focusId,
    ],
  );

  // Fonction fetchData adaptée
  const fetchDataIRCallback = useCallback(
    async (props: {
      sjmoCode: string;
      tableName: string;
      ctrlkey: string;
      currentFocus?: DatatableFocusState;
      query: RSQLCriteria;
      startIndex: number;
      stopIndex: number;
      reset: boolean;
      cancelToken: CancelTokenSource;
      oelColumns: string[];
      filterBar: FilterBar | undefined;
      groupBy?: string;
      groupByFunctions?: GroupByFunctionParameter[];
      aggregates?: AggregateProperties[];
    }): Promise<PagedResourceWithAggregates<Pojo>> => {
      return fetchDataIR({
        ...props,
        breakRows: props.groupBy ? [props.groupBy] : [],
        aggregates: props.aggregates,
        groupByConfig:
          props.groupBy || props.groupByFunctions
            ? {
                columns: props.groupBy ? [props.groupBy] : [],
                functionList: props.groupByFunctions || [],
              }
            : undefined,
        columns: state.columns,
      });
    },
    [state.columns],
  );

  // Gestion du changement de focus
  const onFocusChange = useCallback(
    async (selected: string) => {
      const focusIndex = focusList.findIndex((f) => f.focusId === selected);
      if (focusIndex >= 0 && focusIndex !== state.activeFocus) {
        // Le changement de focus est géré par useInteractiveReports
        focusAction.onFocusChange(focusIndex);
        // Le refresh sera déclenché automatiquement par useInteractiveReports
      }
    },
    [focusList, state.activeFocus, focusAction],
  );

  // Gestion du déplacement des colonnes
  const onColumnMoved = useCallback(
    (event: ColumnMovedEvent) => {
      // movingChanged -> REORDER_COLUMNS
      if (!event.finished) return;

      // `getColumnState` est disponible sur l'API AG Grid, mais pas toujours exposé dans les typings selon versions.
      const orderedColIds = ((event.api as any)?.getColumnState?.() ?? [])
        .map((s) => s.colId)
        // colonne technique ajoutée par `AgGridContainer`
        .filter((colId) => colId && colId !== "satellite");

      const newColumns = orderedColIds
        .map((colId) => state.columns.find((c) => c.column === colId))
        .filter(Boolean) as ComponentState[];

      // Si on n'arrive pas à reconstituer exactement la liste, on ne touche pas l'état.
      if (newColumns.length !== state.columns.length) return;

      dispatch({ type: "REORDER_COLUMNS", payload: newColumns });
    },
    [dispatch, state.columns],
  );

  // Gestion visibilité colonnes
  const onColumnVisible = useCallback(
    (event: ColumnVisibleEvent) => {
      // visibleChanged -> TOGGLE_VISIBILITY_COLUMNS
      const colId = event.column?.getColId();
      if (!colId || colId === "satellite") return;

      const index = state.columns.findIndex((c) => c.column === colId);
      if (index === -1) return;

      columnsAction.toggleVisibility(index);
    },
    [columnsAction, state.columns],
  );

  // Gestion du redimensionnement des colonnes
  const onColumnResized = useCallback(
    (params: ColumnResizedEvent) => {
      // widthChanged -> RESIZE_COLUMN
      const columnName = params.column?.getColId();
      if (!columnName || columnName === "satellite" || !params.finished) return;

      const width = params.column?.getActualWidth();
      if (!width) return;

      columnsAction.onResizeColumnAGGrid(columnName, width);
    },
    [columnsAction],
  );

  // Gestion de la sauvegarde du focus personnalisé
  const onSaveFocusPerso = useCallback(
    async (
      {
        currentFilter,
        currentOrderBy,
        currentPinned,
        currentHighlight,
        currentAggregates,
        currentGroupBy,
        defaultOpenBreakRows,
      }: {
        currentFilter?: FilterModel;
        currentOrderBy?: SortModelItem[];
        currentPinned?: ColumnState[];
        currentHighlight?: HighlightProperties[];
        currentAggregates?: AggregateProperties[];
        currentGroupBy?: {
          columns: string[];
          functionList: GroupByFunctionParameter[];
        };
        defaultOpenBreakRows?: boolean;
      },
      callback: () => void,
    ) => {
      // Conversion des propriétés AG Grid vers InteractiveReportConfiguration
      const propertiesToSave: Partial<InteractiveReportConfiguration> = {};

      // Conversion du tri AG Grid vers sortBy
      if (currentOrderBy && currentOrderBy.length > 0) {
        propertiesToSave.sortBy = currentOrderBy.map((sort) => ({
          column: sort.colId,
          sort: sort.sort === "asc" ? "ASC" : sort.sort === "desc" ? "DESC" : undefined,
        }));
      }

      // Conversion des filtres AG Grid vers filters
      if (currentFilter && Object.keys(currentFilter).length > 0) {
        propertiesToSave.filters = Object.entries(currentFilter).map(([column, value]) => ({
          type: "normal",
          column,
          operator: value.type as RSQLOperator,
          value: value.filter,
          dateFrom: value.dateFrom,
          dateTo: value.dateTo,
        }));
      }

      propertiesToSave.highlights = currentHighlight || [];
      propertiesToSave.currentPin = currentPinned || [];
      propertiesToSave.aggregates = currentAggregates || [];
      propertiesToSave.groupBy = {
        groupBy: currentGroupBy?.columns || [],
        functions: currentGroupBy?.functionList || [],
      };
      propertiesToSave.defaultOpenBreakRows = defaultOpenBreakRows ?? effectiveDefaultOpenBreakRows;

      // Mise à jour du focus actif avec les nouvelles propriétés
      focusAction.updateActiveFocus(propertiesToSave);
      setShowSaveDialog(true);
      callback();
    },
    [focusAction, effectiveDefaultOpenBreakRows],
  );

  // Gestion de la réinitialisation du focus personnalisé
  const onRazFocusPerso = useCallback(
    async (callback: () => void) => {
      if (state.activeFocus < 0 || state.activeFocus >= state.focus.length) {
        callback();
        return;
      }

      const activeFocusConfig = state.focus[state.activeFocus];

      if (activeFocusConfig.typeFocus === "global" || activeFocusConfig.isGlobal === true) {
        toaster.warning(t("commun_interactive_report_focus_global_non_supprimable"));
        callback();
        return;
      }

      const configurationId = activeFocusConfig.id;
      if (configurationId === undefined) {
        callback();
        return;
      }

      try {
        await focusAction.onDelete(configurationId);
      } catch (e) {
        console.error("erreur lors de la suppression du focus personnalisé", e);
      }
      callback();
    },
    [state.activeFocus, state.focus, focusAction],
  );

  // Gestion de la sélection des lignes
  useEffect(() => {
    if (onChangeSelectedEntities) {
      // Convertir les entités sélectionnées depuis l'état d'InteractiveReport
      const selectedEntities: Pojo[] = [];
      state.selectedRows.forEach((index) => {
        const entity = state.entities[index];
        if (entity && entity !== "LOADING_POJO") {
          selectedEntities.push(entity as Pojo);
        }
      });
      onChangeSelectedEntities(selectedEntities);
    }
  }, [state.selectedRows, state.entities, onChangeSelectedEntities]);

  // Rafraîchir la grille quand l'état change (focus, filtres, etc.)
  useEffect(() => {
    if (state.state === "REFETCH" || state.state === "DEFITION_OK") {
      // Le refresh sera déclenché automatiquement par AgGridContainer via fetchData
      // mais on peut forcer un refresh si nécessaire
      if (gridRef.current?.api) {
        gridRef.current.api.refreshServerSide();
      }
    }
  }, [state.state, state.activeFocus, state.search, state.sortBy, state.breakRows]);

  const onChange = useCallback(
    (params: ValueSetterParams<Pojo, string>, waitForSaveRef: boolean) => {
      // Modifier la valeur visible dans la cellule
      params.data[params.colDef.field as string] = params.newValue;
      setDirtyRow((prev) => ({ ...prev, [params.node?.rowIndex ?? 0]: params.data }));
    },
    [setDirtyRow],
  );

  // Rafraîchit le rapport interactif (rechargement des données AG Grid)
  const refresh = useCallback(() => {
    settriggerRefresh((prev) => prev + 1);
  }, []);

  // Enregistre le refresh auprès de la galaxie afin d'être rafraîchi
  // lors d'un refreshListenerAndCallback (sauvegarde, suppression, process...)
  useGalaxieCallbackListener(refresh);

  if (state.columns.length === 0 || gridInitialState == null) {
    return <div>{t("commun_chargement")}</div>;
  }

  return (
    <>
      <AgGridContainer
        ref={gridRef}
        sjmoCode={sjmoCode}
        tableName={state.tableName || ""}
        processOrigine={state.code}
        ctrlkey={ctrlkey}
        definition={{ type: "INTERACTIVE_REPORT", ...definition }}
        currentFocus={currentFocus}
        additionnalClause={staticFiltersClause}
        hideFilterBar={true}
        dirtyRows={dirtyRow}
        insertable={false}
        clearCacheOnClose={clearCacheOnClose}
        initialState={gridInitialState}
        triggerRefresh={triggerRefresh}
        parentHighlights={currentFocus?.highlights || []}
        onChangeParent={onChange}
        getRSQLFilter={getRSQLFilterIR}
        fetchData={fetchDataIRCallback}
        onSaveFocusPerso={onSaveFocusPerso}
        onRazFocusPerso={onRazFocusPerso}
        onFocusChange={onFocusChange}
        onColumnMoved={onColumnMoved}
        onColumnVisible={onColumnVisible}
        onRowClickParent={onRowClickParent}
        onColumnResized={onColumnResized}
        pinnedState={currentFocus?.currentPin || []}
        onChangePinned={(next) => {
          focusAction.updateActiveFocus({ ...state.focus[state.activeFocus], currentPin: next });
        }}
        groupByState={groupByState}
        defaultOpenBreakRows={effectiveDefaultOpenBreakRows}
        onChangeGroupBy={(groupBy) => {
          groupByAction.validateGroupBy(groupBy.columns, groupBy.functionList);
        }}
        onChangeDefaultOpenBreakRows={(next) => {
          focusAction.updateActiveFocus({
            ...state.focus[state.activeFocus],
            defaultOpenBreakRows: next,
          });
        }}
        onChangeAggregates={(next: AggregateProperties[]) => {
          focusAction.updateActiveFocus({ ...state.focus[state.activeFocus], aggregates: next });
        }}
        onAfterSaveDatatable={onAfterSaveDatatable}
        onAfterDeleteDatatable={onAfterDeleteDatatable}
        setSelectedRowsParent={setSelectedRowsParent}
        clearDirtyRows={() => {
          setDirtyRow({});
          settriggerRefresh((prev) => prev + 1);
        }}
        getRowId={getRowId}
      />
      {showSaveDialog && (
        <SaveDialog
          currentFocus={
            state.activeFocus !== -1 && state.focus[state.activeFocus].typeFocus === "user"
              ? state.focus[state.activeFocus]
              : null
          }
          onValidate={(saveProperties) => {
            focusAction.onSave(saveProperties);
            setShowSaveDialog(false);
          }}
          onDelete={focusAction.onDelete}
          onClose={() => setShowSaveDialog(false)}
        />
      )}
    </>
  );
};

export default InteractiveReportAG;
